import { EventEmitter, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IUser } from '../models/users.model';
import { Patterns } from '../enums/patterns.enum';

const FILTERS_PREFIX = 'FILTERS_';

@Injectable({
  providedIn: 'root'
})
export class HelperService {
  collapsedSidebarEmiter: EventEmitter<boolean> = new EventEmitter();

  createCasePopoupVisible!: boolean;
  collapsedSidebar = true;
  isSidebar!: boolean;

  constructor() { }

  deepCopy(obj: any) {
    return JSON.parse(JSON.stringify(obj));
  }

  handleFieldsErrors(formControlValue: FormControl, patternText?: string) {
    let errorTextValue = '';
    if (
      formControlValue
      && formControlValue.invalid
      && (formControlValue.dirty || formControlValue.touched)
      && formControlValue.hasError('required')
    ) {
      errorTextValue = 'label.required_field';
    }
    else if (
      formControlValue
      && formControlValue.invalid
      && (formControlValue.dirty || formControlValue.touched)
      && formControlValue.hasError('email')
    ) {
      errorTextValue = 'label.invalid_email';
    } else if (
      formControlValue
      && formControlValue.invalid
      && (formControlValue.dirty || formControlValue.touched)
      && formControlValue.hasError('pattern')
    ) {
      errorTextValue = patternText ? patternText : 'label.invalid_data';
    }

    return errorTextValue;
  }

  handleLinksErrors(group: string, row: number, form: FormGroup | any, errors: any = {}) {
    form.get(`links.${group}${row}`).controls['isChanged'].setValue(true);

    const titleValue = form.get(`links.${group}${row}`).controls['title'].value;
    const urlValue = form.get(`links.${group}${row}`).controls['url'].value;

    form.get(`links.${group}${row}`).controls['url']
      .setValidators(titleValue ? [Validators.required, Validators.pattern(Patterns.URL)] : Validators.pattern(Patterns.URL));

    form.get(`links.${group}${row}`).controls['title'].setValidators(urlValue ? [Validators.required] : null);

    form.get(`links.${group}${row}`).controls['title'].markAsTouched();
    form.get(`links.${group}${row}`).controls['url'].markAsTouched();
    form.get(`links.${group}${row}`).controls['title'].updateValueAndValidity();
    form.get(`links.${group}${row}`).controls['url'].updateValueAndValidity();


    setTimeout(() => {
      errors[`${group}${row}title`] = this.handleFieldsErrors(form.get(`links.${group}${row}`).controls['title']);
      errors[`${group}${row}url`] = this.handleFieldsErrors(form.get(`links.${group}${row}`).controls['url']);
    });
  }

  enableDisableDependentFieldOnSelect(form: FormGroup, data: any, fieldName: string) {
    const formControl: FormControl = form.get(fieldName) as FormControl;
    data ? formControl.enable() : formControl.disable();
    formControl.markAsPristine();
  }

  convertUsersToStrings(formObject: any, properties: string[], isArray: boolean) {
    if (isArray) {
      properties.forEach((property: string) => {
        formObject[property] = formObject[property]?.map((user: IUser) => {
          return user ? JSON.stringify(user) : user;
        });
      });
    } else {
      properties.forEach((property: string) => {
        formObject[property] = formObject[property] ? JSON.stringify(formObject[property]) : formObject[property];
      });
    }
  }

  convertUsersToObjects(formObject: any, properties: string[], isArray: boolean) {
    if (isArray) {
      properties.forEach((property: string) => {
        formObject[property] = formObject[property] ?? null;
        if (formObject[property]) {
          formObject[property] = formObject[property].map((userString: string) => {
            return JSON.parse(userString);
          });
        }
      });
    } else {
      properties.forEach((property: string) => {
        formObject[property] = formObject[property] ?? null;
        if (formObject[property]) {
          formObject[property] = JSON.parse(formObject[property]);
        }
      });
    }
  }

  getNameOfUserFromUserJsonString(jsonString: string) {
    const splitted: string[] = jsonString.split(';');
    const arr = splitted.map((item: string) => {
      return JSON.parse(item).fullName;
    })
    return arr.toString().replace(/,/g, ', ');
  }

  getNameOfUserFromUserArrayJsonString(jsonString: string) {
    const array: IUser[] = JSON.parse(jsonString).map((i: IUser) => { return i.fullName });
    return array.toString().replace(/,/g, ', ');
  }

  getUserByUserId(id: number, users: IUser[]): string {
    return users.find(user => user.id === id)?.fullName ?? '';
  }

  isDateInThePast(dateToCheck: any) {
    const today = new Date().setHours(0, 0, 0, 0);
    dateToCheck = new Date(dateToCheck as Date).setHours(0, 0, 0, 0);

    return dateToCheck < today;
  }

  isDateInTheFuture(dateToCheck: any) {
    const today = new Date().setHours(0, 0, 0, 0);
    dateToCheck = new Date(dateToCheck as Date).setHours(0, 0, 0, 0);

    return dateToCheck > today;
  }

  convertToMidnightUTC(value: string | Date | null): string | null {
    if (!value) return null;

    const date = new Date(value);
    const utcMidnight = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return utcMidnight.toISOString();
  }

  adjustDateFormatToUTCByAddingZ(value: any) {
    return value && !value.toString().includes('Z') ? `${value}Z` : value;
  }

  checkIfUserExistsInArray(array: IUser[], userId: number): boolean {
    return array.some(user => user.id === userId);
  }

  getUsersListWithSelectedUser(usersList: IUser[], selectedUserId: number, selectedUserName: string): IUser[] {
    if (!(selectedUserId && selectedUserName)) {
      return usersList;
    }
    return this.checkIfUserExistsInArray(usersList, selectedUserId) ? usersList : [{ id: selectedUserId, fullName: selectedUserName } as IUser, ...usersList];
  }

  storeGridFilterObjectToSessionStorage(gridType: string, filterState: any) {
    if (gridType) {
      sessionStorage.setItem(`${FILTERS_PREFIX}${gridType}`, JSON.stringify(filterState));
    }
  }

  getGridFilterObjectFromSessionStorage(gridType: string) {
    const filterFromStorage: string | undefined = sessionStorage.getItem(`${FILTERS_PREFIX}${gridType}`) ?? undefined;
    return filterFromStorage ? JSON.parse(filterFromStorage) : filterFromStorage;
  }

  getColorOfNextActionDueDate(nextActionDueDate: string): string {
    if (!nextActionDueDate) return '';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const date = new Date(nextActionDueDate);
    date.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (date < today) {
      return 'value__highlight__red';
    }

    if (date.getTime() <= tomorrow.getTime()) {
      return 'value__highlight__orange';
    }

    return '';
  }

  saveFile(file: any, blob: any) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.fileName || file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  replaceFilterBlanksSelection(data: any) {
    return {
      ...data,
      datatableFilters: data.datatableFilters?.map((filter: any) => ({
        ...filter,
        columnSelectedValues: filter.columnSelectedValues?.map(
          (value: string) => value === '[ Blanks ]' ? '' : value
        )
      }))
    };
  }
  replaceFilterBlanksSelectionMultipleSearch(data: any) {
    return {
      ...data,
      fieldFilters: data.fieldFilters?.map((filter: any) => ({
        ...filter,
        columnSelectedValues: filter.columnSelectedValues?.map(
          (value: string) => value === '[ Blanks ]' ? '' : value
        )
      }))
    };
  }

}
